import { FormattedMessage } from 'react-intl';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { InstantBuy } from '@sorare/core/src/atoms/icons/InstantBuy';
import { iconChevronRight } from '@sorare/core/src/atoms/icons/system/iconChevronRight.ts';
import { iconLightningBolt } from '@sorare/core/src/atoms/icons/system/iconLightningBolt.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMatchdayPicksTab } from '@sorare/core/src/hooks/market/useMatchdayPicksTab';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import background from './assets/background.webp';
import picks from './assets/picks.webp';
import picksMobile from './assets/picks_mobile.webp';

const root = css`
  position: relative;
  width: 100%;
  min-height: calc(31 * var(--unit));
  padding: var(--double-unit);
  overflow: hidden;
  /* A transparent border reveals the border-box layer, which is how CSS fakes a
     gradient stroke. */
  border: 1px solid transparent;
  border-radius: var(--unit);
  /* The asset is a wide, short pattern, so covering a tall mobile banner blows it
     up: tile it at the scale the desktop banner renders it at instead. */
  --banner-background-layout: top center / auto calc(20 * var(--unit)) repeat;
  background:
    url(${background}) var(--banner-background-layout) padding-box,
    linear-gradient(0deg, var(--c-modes-pro-tertiary) 0%, var(--c-nd-150) 45%)
      border-box;

  @media ${tabletAndAbove} {
    --banner-background-layout: bottom center / cover no-repeat;

    min-height: 0;
  }
`;

const artwork = css`
  display: none;

  @media ${tabletAndAbove} {
    display: block;
    align-self: flex-end;
    /* Cancels the banner padding so the cards sit flush on the bottom edge. */
    margin-bottom: calc(-1 * var(--double-unit));
    height: calc(15 * var(--unit));
    object-fit: contain;
    object-position: bottom;
  }
`;

const mobileArtwork = css`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 80%;

  @media ${tabletAndAbove} {
    display: none;
  }
`;

/* Sits between the cards and the wording so the text stays legible over them. */
const scrim = css`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(var(--c-rgb-black), 0) 0%,
    var(--c-black) 100%
  );
  height: 50%;
  top: 50%;

  @media ${tabletAndAbove} {
    display: none;
  }
`;

/* Mobile stacks the wording under the cards, so the row only applies from tablet. */
const layout = css`
  position: relative;
  align-items: flex-end;
  min-height: inherit;

  @media ${tabletAndAbove} {
    align-items: center;
  }
`;

const cta = css`
  position: absolute;
  top: 0;
  right: 0;

  @media ${tabletAndAbove} {
    position: static;
  }
`;

const callToAction = css`
  display: none;

  @media ${tabletAndAbove} {
    display: flex;
  }
`;

const chevron = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(5 * var(--unit));
  width: calc(5 * var(--unit));
  border: 1px solid var(--c-nd-150);
  border-radius: 999px;
`;

export const FlashSalesBanner = ({ sport }: { sport: Sport }) => {
  const { generateSportContextPath } = useSportContext();
  const { show } = useMatchdayPicksTab({ sport });

  if (!show) {
    return null;
  }

  return (
    <ButtonBase
      className={root}
      to={generateSportContextPath(ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS)}
    >
      <img className={mobileArtwork} src={picksMobile} alt="" />
      <div className={scrim} />
      <SBHorizontal gap={2} className={layout}>
        <Vertical gap={0.5} className="text-left">
          <Horizontal gap={0.5}>
            <FontAwesomeIcon
              icon={iconLightningBolt}
              color="var(--c-modes-pro-tertiary)"
            />
            <BrandM color="var(--c-modes-pro-tertiary)">
              <FormattedMessage defaultMessage="Flash sales" />
            </BrandM>
          </Horizontal>
          <BrandM>
            <FormattedMessage defaultMessage="Locked prices" />
          </BrandM>
        </Vertical>

        <img className={artwork} src={picks} alt="" />

        <Horizontal gap={2} center className={cta}>
          <Horizontal gap={1} center className={callToAction}>
            <InstantBuy size={4} color="var(--c-modes-pro-tertiary)" />
            <BrandS className="text-left">
              <FormattedMessage defaultMessage="While supplies last" />
            </BrandS>
          </Horizontal>
          <div className={chevron}>
            <FontAwesomeIcon icon={iconChevronRight} />
          </div>
        </Horizontal>
      </SBHorizontal>
    </ButtonBase>
  );
};
